// Feature-aware access control for the EcoIntel platform.
//
// Phase 0 substrate. Replaces the old binary "subscribed or not" gate with
// per-feature checks driven by:
//   - user.planTier (FREE | PAID)
//   - user.subscriptionOverride (admin grant of paid tier)
//   - user.reportPackageOverrides (admin grant of specific report packages)
//   - user.reportGrants ({ [packageId]: { years: [2024, 2025] } }) for Stripe-driven access
//   - user.role (admin always wins)
//   - per-farm context (isPublicDemo flag unlocks viewing on the demo property)
//
// The intent is that NO call site outside this file decides feature access by
// hand. Always go through `hasFeature(...)` or `hasReportPackageAccess(...)`.
//
// Per-feature gating implementation (which UI components actually consult these
// helpers) is rolled out alongside each Phase that needs it. Until then the
// helpers can be imported and used safely; they return sensible defaults.

import { ROLES, PLAN_TIERS, resolvePlanTier, isPaidTier } from './roles';

/**
 * Canonical feature identifiers. Use these constants rather than string
 * literals so the linter catches typos.
 */
export const FEATURES = {
  // Map / visualisation
  MAP_MULTI_LAYER: 'map_multi_layer',
  MAP_LAYER_CONTROLS: 'map_layer_controls',
  // Analytics
  VIEW_PARCEL_ANALYTICS: 'view_parcel_analytics',
  // Property management
  ADD_PROPERTY: 'add_property',
  EDIT_PROPERTY_CONFIG: 'edit_property_config',
  // Reports
  PURCHASE_REPORTS: 'purchase_reports',
  VIEW_REPORTS_TAB: 'view_reports_tab',
};

/**
 * Decide whether a given user can use a feature.
 *
 * @param {object|null} userData - the user's Firestore document (or null while loading)
 * @param {string} feature - one of FEATURES.*
 * @param {object} [opts]
 * @param {object} [opts.farm] - the farm doc currently in scope, if relevant
 * @returns {boolean}
 */
export function hasFeature(userData, feature, opts = {}) {
  if (!userData) return false;
  if (userData.role === ROLES.ADMIN) return true;

  const farm = opts.farm || null;
  const isPublicDemoFarm = !!(farm && farm.isPublicDemo);
  const paid = isPaidTier(userData);

  switch (feature) {
    // Map multi-layer + advanced controls: paid only. (Free users see a single
    // radio-style layer picker with no opacity/stacking controls.)
    case FEATURES.MAP_MULTI_LAYER:
    case FEATURES.MAP_LAYER_CONTROLS:
      return paid;

    // Per-parcel analytics: paid for own farms; free if viewing the demo farm.
    case FEATURES.VIEW_PARCEL_ANALYTICS:
      if (paid) return true;
      if (isPublicDemoFarm) return true;
      return false;

    // Adding a property: free users can add 1 (excluding the demo); paid users
    // get 1 with their subscription and can buy additional-property add-ons.
    // The cap check is enforced by the caller (it needs to count current farms).
    // This helper just answers "are you ALLOWED to attempt to add at all".
    case FEATURES.ADD_PROPERTY:
      return true; // capped further by the caller

    // Editing property config: only the owner / admin. Free users editing their
    // own future property is fine; demo property is read-only for non-admins.
    case FEATURES.EDIT_PROPERTY_CONFIG:
      if (userData.role === ROLES.DEMO) return false;
      if (farm && farm.isPublicDemo) return false; // demo is read-only to non-admins
      return true;

    // Purchasing reports: paid users only. Free users see an upgrade CTA.
    case FEATURES.PURCHASE_REPORTS:
      return paid;

    // Viewing the reports tab: surfaced for paid users; free users see nothing
    // until they pay (per the payment-first flow agreed in the spec doc).
    case FEATURES.VIEW_REPORTS_TAB:
      return paid;

    default:
      // Unknown feature: deny by default and warn loudly in dev.
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`hasFeature: unknown feature key "${feature}"`);
      }
      return false;
  }
}

/**
 * Decide whether a user has access to a particular annual edition of a report
 * package. Returns true if:
 *   - admin, OR
 *   - user resolves to the paid plan tier (Stripe active/cancelling, admin
 *     subscriptionOverride, or planTier === 'paid'). Property-level scoping
 *     is the caller's responsibility — this function is only consulted for
 *     properties the user can already see (FarmReportsPage is reached via
 *     the `farms/{farmId}` route and routing is gated by `canSeeFarm`; the
 *     backend mirror in marcusIngestion.userHasReportPackage is gated by
 *     `userAuthorizedForFarm` before this check fires), OR
 *   - reportPackageOverrides contains the packageId (admin-granted package
 *     access for free-tier users), OR
 *   - reportGrants[packageId].years includes the year (legacy / Stripe-
 *     driven per-package grants — kept for back-compat with existing per-
 *     edition entitlements created before subscription unlocked all reports).
 *
 * Years are granted permanently per the spec: a user keeps every annual edition
 * they had access to even if they later cancel.
 *
 * @param {object|null} userData
 * @param {string} packageId
 * @param {number|string} year
 */
export function hasReportPackageAccess(userData, packageId, year) {
  if (!userData || !packageId) return false;
  if (userData.role === ROLES.ADMIN) return true;
  if (isPaidTier(userData)) return true;
  const numericYear = Number(year);
  const overrides = Array.isArray(userData.reportPackageOverrides)
    ? userData.reportPackageOverrides
    : [];
  if (overrides.includes(packageId)) return true;
  const grants = userData.reportGrants || {};
  const pkgGrants = grants[packageId];
  if (pkgGrants && Array.isArray(pkgGrants.years) && pkgGrants.years.includes(numericYear)) {
    return true;
  }
  return false;
}

/**
 * Farm-aware report entitlement (2026-05-31 product decision: report access
 * follows PROPERTY access).
 *
 * Resolves whether `userData` may view/download a given report edition on a
 * specific property. Layers, in order:
 *   - admin                                   → always.
 *   - farm.isPublicDemo === true              → open to EVERY signed-in user.
 *                                               This is the demo carve-out that
 *                                               fixes free users (e.g. demo
 *                                               viewers) seeing every demo
 *                                               report locked behind
 *                                               "Subscribe to unlock".
 *   - active subscription (isPaidTier)        → unlocks all reports on any
 *                                               property the user can see. An
 *                                               EXPIRED subscription
 *                                               (past_due / cancelled / none →
 *                                               resolvePlanTier === free) falls
 *                                               through and re-locks non-demo
 *                                               reports, per the explicit
 *                                               "gate when subscription
 *                                               expires" requirement.
 *   - explicit per-package grants/overrides   → additive back-compat path for
 *                                               free-tier users (see
 *                                               hasReportPackageAccess).
 *
 * Property-read access itself is enforced by routing + Firestore rules on the
 * way in and by `userAuthorizedForFarm` on the backend download endpoint, so
 * this helper intentionally does not re-litigate Layer-1 access — it only
 * decides report entitlement for a property the caller can already reach.
 *
 * Backend mirror: the `/reports/.../download` endpoint in
 * backend/analytics-server/index.js (admin || public-demo ||
 * userHasReportPackage). Keep the two in lock-step.
 *
 * @param {object|null} userData
 * @param {object|null} farm - the farm doc in scope ({ id, isPublicDemo, ... })
 * @param {string} packageId
 * @param {number|string} year
 */
export function hasReportAccess(userData, farm, packageId, year) {
  if (!userData) return false;
  if (userData.role === ROLES.ADMIN) return true;
  if (farm && farm.isPublicDemo === true) return true;
  if (isPaidTier(userData)) return true;
  if (packageId && hasReportPackageAccess(userData, packageId, year)) return true;
  return false;
}

/**
 * Convenience: is this farm visible to the user as a property they should see
 * in their dashboard / sidebar?
 *
 * The two pathways are:
 *  - farm is in user.farmIds (their own property, or assigned by admin), OR
 *  - farm.isPublicDemo === true (the global freemium demo).
 *
 * Admins see everything regardless.
 */
export function canSeeFarm(userData, farm) {
  if (!userData || !farm) return false;
  if (userData.role === ROLES.ADMIN) return true;
  if (farm.isPublicDemo) return true;
  const farmIds = Array.isArray(userData.farmIds) ? userData.farmIds : [];
  return farmIds.includes(farm.id);
}

// Convenience re-exports so callers only need to import from one place.
export { PLAN_TIERS, resolvePlanTier, isPaidTier };
